import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useDeleteOpuSession } from '../contexts/deletions';

// Confirmación de borrado en CASCADA de una sesión OPU (admin-aspiration-sessions.md · 0127).
// Acción sensible → doble barrera (checkbox + botón peligro), useModalDialog (enfoca Cancelar).
const CASCADE = [
    ['aspirations_count', 'Aspiraciones'],
    ['clivages_count',    'Clivages'],
    ['embryos_count',     'Embriones'],
    ['transfers_count',   'Transferencias'],
    ['pregnancies_count', 'Preñeces'],
];

export default function DeleteOpuModal({ session, onClose, onDeleted }) {
    const cardRef = useModalDialog(onClose);
    const { mutateAsync: remove, isPending: busy } = useDeleteOpuSession();
    const [ack, setAck] = useState(false);

    const counts = session.counts ?? {};
    const rows = CASCADE.filter(([k]) => (Number(counts[k]) || 0) > 0);

    const submit = async () => {
        if (!ack || busy) return;
        try {
            await remove(session.id);
            toast.success(`Sesión ${session.code} eliminada (cascada)`);
            onDeleted?.();
        } catch (er) {
            if (er?.status === 409) toast.error('No se puede eliminar: hay un parto activo aguas abajo.');
            else if (er?.status === 404) toast.error('La sesión ya no existe.');
            else if (er?.status === 400) toast.error('Falta confirmación.');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar la sesión');
        }
    };

    return (
        <div className="mrg-modal" role="dialog" aria-modal="true" aria-labelledby="del-modal-title">
            <div className="mrg-modal__overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
            <div className="mrg-modal__card" ref={cardRef}>
                <header className="mrg-modal__head">
                    <h2 className="mrg-modal__title" id="del-modal-title">Eliminar sesión {session.code}</h2>
                    <button type="button" className="mrg-modal__close" onClick={onClose} disabled={busy} aria-label="Cancelar"><IconClose /></button>
                </header>

                <p className="mrg-modal__summary">
                    Se apagará la sesión <strong>{session.code}</strong> y <strong>toda su descendencia</strong> en cascada
                    (soft-delete, reversible sólo de forma manual).
                </p>

                {rows.length > 0 ? (
                    <ul className="mrg-bars">
                        {rows.map(([k, label]) => (
                            <li key={k} className="mrg-bar mrg-bar--flat">
                                <span className="mrg-bar__label">{label}</span>
                                <span className="mrg-bar__count">{counts[k]}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mrg-modal__released">La sesión no tiene descendencia registrada; sólo se dará de baja ella.</p>
                )}

                <div className="mrg-modal__warn" role="note">
                    <strong>Acción sensible.</strong> Apaga aspiraciones, clivages, embriones, transferencias y preñeces asociadas;
                    las asignaciones de encargo se <strong>liberan</strong>. No hay «deshacer» automático.
                </div>

                <label className="mrg-modal__ack">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} />
                    <span>Entiendo el alcance de la cascada y confirmo la eliminación.</span>
                </label>

                <footer className="mrg-modal__foot">
                    <button type="button" className="mrg-btn mrg-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="mrg-btn mrg-btn--danger" onClick={submit} disabled={busy || !ack}>{busy ? 'Eliminando…' : 'Eliminar en cascada'}</button>
                </footer>
            </div>
        </div>
    );
}
